<script>
import { mapGetters } from 'vuex';

import Layout from '@layouts/main.vue';
import ListTick from '@assets/icons/list-tick.svg';
import ExampleSection from '@components/pass-and-card/example-section.vue';
import PassGrid from '@components/pass-and-card/pass-grid.vue';

import Translatable from '@src/mixins/translatable.js';
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js';
import ImageMixin from '@src/mixins/imageMixin.js';
import Resizable from '@src/mixins/resizable.js';
import AuthorLabel from '@src/mixins/author.js';
import { sharedBlocksMixin } from '@src/mixins/sharedBlocksMixin.js';
import SeoMixin from '@src/mixins/seo.js';
import ProductFactsMixin from '@src/mixins/product-facts.js';
import { buildPassBenefits } from '@utils/structured-data';

import SharedBlockSlot from '@components/sharedBlocks/SharedBlockSlot.vue';

export default {
  // page: {
  //   title: "What's included?",
  //   meta: [
  //     {
  //       name: 'description',
  //       content:
  //         'Free entry to 80+ attractions, Free bus tour and river cruises, Special Offers, Free mobile app, Optional transport tickets',
  //     },
  //   ],
  // },
  components: {
    Layout,
    ExampleSection,
    PassGrid,
    SharedBlockSlot,
  },
  mixins: [
    Translatable,
    SanitizerMixin,
    ImageMixin,
    Resizable,
    AuthorLabel,
    sharedBlocksMixin,
    ProductFactsMixin,
    SeoMixin,
  ],
  computed: {
    ...mapGetters('passAndCard', ['pageData']),
    ...mapGetters('reviews', ['averageRating', 'fifteenRandomReviews']),
    // The only page that prints "What you get", so the only one that adds it
    // to the shared product node. Reads the very method the template renders
    // from, so the list cannot drift from what a visitor sees.
    seoProductFacts() {
      const facts = {};

      const additionalProperty = buildPassBenefits(this.pageContent('benefits'));
      if (additionalProperty.length) facts.additionalProperty = additionalProperty;

      // The rating has to be stated here too, not because this page needs a
      // rich result but because `additionalProperty` alone cannot carry a
      // Product node: Google requires one of review/aggregateRating/offers.
      // The shared reviews block prints the score, so saying it is honest.
      if (this.seoSharedSections.isVisibleSharedReviews && this.seoPassRating) {
        facts.aggregateRating = this.seoPassRating;
      }

      return Object.keys(facts).length ? facts : null;
    },

    seoEntity() {
      return {
        ...this.pageData,
        isStatic: true,
      };
    },
    getListStyleTick() {
      return {
        listStyleImage: `url(${ListTick})`,
      };
    },
    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen ? this.pageData.mainImage.web : this.pageData.mainImage.app;
      }
      return null;
    },
    benefitImage() {
      const imagePath = this.pageData?.benefit_web_image;
      return imagePath ? `${process.env.VUE_APP_STATIC_URL}${imagePath}` : '';
    },
  },
  watch: {
    pageData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.slugs) {
          this.$store.commit('translations/SET_ALTERNATE_SLUGS', newVal.slugs);
        }
      },
    },
  },
  provide() {
    const provided = {};
    Object.defineProperty(provided, 'sortedSharedBlocks', {
      enumerable: true,
      get: () => this.sortedSharedBlocks,
    });
    return {
      sharedBlocksProvider: provided,
    };
  },
  methods: {
    getSeoText(obj) {
      if (!obj) return '';
      return obj[this.currentLanguage] || obj.en || '';
    },
    pageContent(prop) {
      if (!this.pageData.content) {
        return [];
      }
      const localizedContent = this.pageData.content[this.currentLanguage];
      if (localizedContent && typeof localizedContent[prop] !== 'undefined') {
        if (Array.isArray(localizedContent[prop]) && localizedContent[prop].length === 0) {
          return this.pageData.content.en?.[prop] || [];
        }
        return localizedContent[prop];
      }
      return this.pageData.content.en?.[prop] || [];
    },
    getContent(path) {
      const pathArray = path.split('.');

      if (!this.pageData?.content) return '';

      const getValue = (obj, p) => p.reduce((acc, key) => acc?.[key], obj);

      const localizedValue = getValue(this.pageData.content[this.currentLanguage], pathArray);

      if (localizedValue) {
        return localizedValue;
      }

      return getValue(this.pageData.content.en, pathArray) || '';
    },
    getSharedTitle(fieldName) {
      const sharedFieldObject = this.pageData?.[fieldName];
      if (!sharedFieldObject) {
        return '';
      }

      const localizedTitle = sharedFieldObject[this.currentLanguage]?.title;

      if (localizedTitle) {
        return localizedTitle;
      }

      return sharedFieldObject.en?.title || '';
    },
  },
};
</script>

<template>
  <Layout>
    <section
      v-if="pageData && pageData.content && pageImg"
      class="header justify-center"
      :style="getBackground(pageImg.url)"
      :title="getSeoText(pageImg.title)"
      role="img"
      :aria-label="getSeoText(pageImg.alt)"
    >
      <div class="header-block">
        <h1 class="header-title" v-html="getTranslatedContent('title', pageData.content)"></h1>
        <h3
          class="header-subtitle"
          v-html="getTranslatedContent('subtitle', pageData.content)"
        ></h3>
      </div>
    </section>
    <section id="what-you-get" class="description-banner justify-center">
      <p class="author container" v-html="authorLabel(pageData)"></p>
      <div class="description">
        <div class="description-card">
          <div class="banner-content">
            <h3
              class="banner-title"
              v-html="getTranslatedContent('benefits_title', pageData.content)"
            ></h3>
            <div
              class="banner-text"
              v-html="getTranslatedContent('benefits_description', pageData.content)"
            ></div>
          </div>
          <div class="new-banner-container">
            <img v-if="benefitImage" :src="benefitImage" alt="CoolPass Benefits" />
          </div>
        </div>
        <div class="description-card-list">
          <div class="tips-details">
            <ul :style="getListStyleTick">
              <template v-for="(benefit, index) in pageContent('benefits')">
                <li :key="benefit.title + benefit.text + index" v-html="benefit.title"></li>
                <div
                  v-if="benefit.text"
                  :key="benefit.text + index"
                  class="subtext"
                  v-html="benefit.text"
                ></div>
              </template>
            </ul>
          </div>
        </div>
      </div>
    </section>
    <section class="grid-section justify-center">
      <PassGrid />
    </section>
    <section id="how-you-save" class="steps-section justify-center">
      <div class="container">
        <h3 class="section-title" v-html="getContent('example_title_days')"></h3>
        <div class="saving-text" v-html="pageContent('example_text_days')"></div>
        <h4 class="using-title" v-html="pageContent('example_subtitle_days')"></h4>
        <ExampleSection />
      </div>
    </section>
    <SharedBlockSlot :slot-index="1" />
    <SharedBlockSlot :slot-index="2" />
    <SharedBlockSlot :slot-index="3" />
    <SharedBlockSlot :slot-index="4" />
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.header-block {
  width: 840px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
  margin-top: 217px;
  margin-right: 300px;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin-left: 22px;
    color: $white;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin-left: 25px;
    color: $white;
  }
}

.description-banner {
  margin: -99px 0 60px;
  position: relative;
  & .description {
    width: 1140px;
    min-height: 475px;
    height: auto;
    display: flex;
    flex-direction: row;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
  }
}

.description-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  margin: 31px 0 0 70px;
}

.banner-content {
  margin-right: 15px;
}

.new-banner-container {
  max-width: 521px;
  width: 100%;
  margin: 0 auto;
  aspect-ratio: 521 / 311;
}

.new-banner-container img {
  width: 100%;
  height: 100%;
  display: block;

  object-fit: contain;

  //  object-fit: cover; // or cut
}

// .banner-images {
//   display: flex;
//   align-items: center;
// }

// .banner-card {
//   height: 150px;
//   width: 230px;
//   background: url('../../../assets/images/prague-card-rounded.png') no-repeat
//     center;
//   background-size: cover;
//   border-radius: 10px;
//   margin-bottom: 12px;
// }

// .banner-phone {
//   height: 311px;
//   width: 291px;
//   background: url('../../../assets/images/phone-with-hand-right.png') no-repeat
//     center;
//   background-size: cover;
// }

.banner-text {
  width: 500px;
}

.description-card-list {
  border-radius: 0px 20px 20px 0px;
  width: 529px;
  padding: 34px 32px;
  background: $dark-blue;
}

.tips-details {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 15px;
  user-select: none;
}

.tips-details ul li {
  font-family: Open Sans Semibold, sans-serif;
  font-weight: normal;
  font-size: 16px;
  line-height: 23px;
  color: $white;
  margin-bottom: 5px;
  padding-left: 10px;
}

.subtext {
  font-family: Open Sans, sans-serif;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 130%;
  color: white;
  margin-bottom: 20px;
  padding-left: 10px;
}

.section-title,
.reviews-title,
.banner-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  text-transform: uppercase;
  color: $dark-blue;
  margin-bottom: 15px;
}

.banner-title {
  margin-bottom: 20px;
}

.section-title {
  margin-top: 80px;
}

.using-title {
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 18px;
  line-height: 23px;
  text-transform: uppercase;
  color: $dark-blue;
  margin-top: 30px;
  margin-bottom: 30px;
}

::v-deep .banner-text,
::v-deep .saving-text {
  font-family: Open Sans, sans-serif;
  font-weight: normal;
  font-size: 14px;
  line-height: 23px;
  color: $black;
  user-select: none;

  & p:empty {
    height: 23px;
    margin: 0;
  }

  & p:not(:last-child):not(:empty) {
    margin-bottom: 23px;
  }

  & p > br {
    display: none;
  }
  & p:has(br:last-child)::after {
    content: '';
    display: block;
    height: 23px;
  }

  & em {
    font-style: italic;
  }

  & a {
    color: $gray;
    text-decoration: none;

    &:hover {
      color: $orange;
    }
  }

  & ul {
    margin-left: 20px;
    list-style: initial;
  }

  & strong {
    font-family: Open Sans Bold, sans-serif;
    font-style: normal;
    font-weight: bold;
  }

  & .ql-align-center {
    text-align: center;
  }
}

.reviews-section {
  margin-top: 93px;
}

.reviews-section-header {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
}

.reviews-title {
  margin-top: 15px;
}

.rating-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-left: auto;
}

.average-review-rating {
  margin-left: auto;
  margin-right: 12px;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-size: 22px;
  line-height: 29px;
  color: $dark-blue;
}

@media only screen and (max-width: $screen-width-xs) {
  .main-container {
    width: 100%;
  }

  .header {
    height: 244px;
  }

  .header-block {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    padding-top: 0;
    height: 125px;

    .header-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 0px 8px 10px;
      color: $white;
    }

    .header-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
    }
  }

  .description-banner {
    margin: -20px 0 0px;

    & .description {
      flex-direction: column;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: auto;
      padding: 0;
    }
  }

  .description-card {
    width: 100%;
    flex-direction: column;
    align-items: center;
    margin: 0;
  }

  // .banner-images {
  //   display: flex;
  //   align-items: center;
  //   justify-content: space-between;
  //   width: 100%;
  // }

  // .banner-card,
  // .banner-phone {
  //   margin-top: 0;
  // }

  // .banner-phone {
  //   height: 207px;
  //   width: 180px;
  // }

  .banner-card {
    height: 120px;
    width: 174px;
    margin-left: 13px;
    border-radius: 10px;
  }

  .banner-content {
    text-align: center;
    margin: 0;
    padding: 0 8px;
  }

  .banner-title {
    text-align: left;
    margin: 15px 0 15px;
    padding: 0;
    width: 100%;
    font-size: 18px;
    line-height: 23px;
  }

  .banner-text {
    width: 100%;
    text-align: left;
  }

  .description-card-list {
    border-radius: 0px;
    width: 100%;
    padding: 34px 0;
    background: $dark-blue;
    border-radius: 0 0 20px 20px;
  }

  .tips-details {
    margin: 0 12px 0 30px;
  }

  .tips-details ul li {
    margin-bottom: 5px;
  }

  .section-title {
    margin-top: 40px;
  }

  .using-title {
    margin-top: 30px;
  }

  .reviews-section {
    margin-top: 66px;
  }

  .reviews-section-header {
    flex-direction: column;
  }

  .rating-wrapper {
    margin: 0 auto 0 0;
    justify-content: start;
  }

  .average-review-rating {
    margin-right: 12px;
  }
}
</style>
